import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from './api';
import type { AdminUser } from './types';
import UserWalletModal from './components/UserWalletModal';

// Phase C2：帳號皆為 domain-profile（身份權威在 auth-service）。
// 已移除本地建帳號 / 重設密碼 / 管理員角色欄位——此頁僅檢視與編輯領域資料（額度 / 到期 / 里 ID 等）。

// 將 ISO 字串轉換為 datetime-local input 的值（本地時間，格式 yyyy-MM-ddTHH:mm）
const toDatetimeLocalValue = (val?: string | null): string => {
  if (!val) return '';
  const d = new Date(val);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 將 datetime-local 值（本地時間）轉換為帶時區偏移的 ISO 字串，例如 2026-05-01T23:59:00+08:00
const toISOWithTZ = (localStr: string): string => {
  if (!localStr) return '';
  const d = new Date(localStr);
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  const tzHours = pad(offset / 60);
  const tzMins = pad(offset % 60);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00${sign}${tzHours}:${tzMins}`;
};

const OwnerNameCell: React.FC<{ user: AdminUser; onSaved: () => void }> = ({ user, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.ownerName ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const save = async () => {
    if (value === (user.ownerName ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await adminApi.updateOwnerName(user._id, value);
      onSaved();
    } catch {
      setValue(user.ownerName ?? '');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') { setValue(user.ownerName ?? ''); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        disabled={saving}
        className="border dark:border-gray-600 rounded px-2 py-0.5 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 border-b border-dashed border-gray-300 dark:border-gray-600"
      title="點擊編輯"
    >
      {value || <span className="text-gray-400 dark:text-gray-500 italic text-xs">未設定</span>}
    </span>
  );
};

const ExpiresAtCell: React.FC<{ user: AdminUser; onSaved: () => void }> = ({ user, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(toDatetimeLocalValue(user.expiresAt));
  const [saving, setSaving] = useState(false);

  const isExpired = user.expiresAt ? new Date() > new Date(user.expiresAt) : false;

  const save = async () => {
    const original = toDatetimeLocalValue(user.expiresAt);
    if (value === original) { setEditing(false); return; }
    setSaving(true);
    try {
      await adminApi.updateExpiresAt(user._id, value ? toISOWithTZ(value) : null);
      onSaved();
    } catch {
      setValue(original);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        type="datetime-local"
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(toDatetimeLocalValue(user.expiresAt)); setEditing(false); } }}
        disabled={saving}
        className="border dark:border-gray-600 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
      />
    );
  }

  if (!user.expiresAt) {
    return (
      <span
        onClick={() => setEditing(true)}
        className="cursor-pointer text-xs text-gray-400 dark:text-gray-500 italic hover:text-blue-600 dark:hover:text-blue-400"
        title="點擊設定到期日"
      >
        永久有效
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-pointer text-xs border-b border-dashed ${
        isExpired
          ? 'text-red-500 dark:text-red-400 border-red-300 dark:border-red-700'
          : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:text-blue-600 dark:hover:text-blue-400'
      }`}
      title={isExpired ? '已過期，點擊修改' : '點擊修改'}
    >
      {new Date(user.expiresAt).toLocaleString('zh-TW')}
      {isExpired && <span className="ml-1 text-red-500">（已過期）</span>}
    </span>
  );
};

const FreeQuotaCell: React.FC<{ user: AdminUser; onSaved: () => void }> = ({ user, onSaved }) => {
  const initial = user.freeQuota == null ? '' : String(user.freeQuota);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const save = async () => {
    if (value === initial) { setEditing(false); return; }
    // 留空 → null（沿用系統預設值）
    const payload = value.trim() === '' ? null : Number(value);
    if (payload !== null && (!Number.isFinite(payload) || payload < 0)) {
      setValue(initial);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateFreeQuota(user._id, payload);
      onSaved();
    } catch {
      setValue(initial);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setValue(initial); setEditing(false); }
        }}
        disabled={saving}
        placeholder="預設"
        className="border dark:border-gray-600 rounded px-2 py-0.5 text-sm w-24 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer border-b border-dashed border-gray-300 dark:border-gray-600 hover:text-blue-600 dark:hover:text-blue-400"
      title="點擊編輯（留空使用系統預設值）"
    >
      {user.freeQuota == null
        ? <span className="text-xs text-gray-400 dark:text-gray-500 italic">預設</span>
        : <span className="text-gray-700 dark:text-gray-300">{user.freeQuota}</span>}
    </span>
  );
};

const JoleehiVillageIdCell: React.FC<{ user: AdminUser; onSaved: () => void }> = ({ user, onSaved }) => {
  const [stage, setStage] = useState<'idle' | 'confirming' | 'editing'>('idle');
  const [value, setValue] = useState(user.joleehiVillageId ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === 'editing') inputRef.current?.focus();
  }, [stage]);

  const save = async () => {
    const original = user.joleehiVillageId ?? '';
    if (value === original) { setStage('idle'); return; }
    setSaving(true);
    try {
      await adminApi.updateJoleehiVillageId(user._id, value.trim() === '' ? null : value.trim());
      onSaved();
    } catch {
      setValue(original);
    } finally {
      setSaving(false);
      setStage('idle');
    }
  };

  const cancel = () => {
    setValue(user.joleehiVillageId ?? '');
    setStage('idle');
  };

  if (stage === 'editing') {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        disabled={saving}
        placeholder="留空以清除"
        className="border dark:border-gray-600 rounded px-2 py-0.5 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
      />
    );
  }

  if (stage === 'confirming') {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-amber-600 dark:text-amber-400">變更會影響登入綁定，確定？</span>
        <button
          onClick={() => setStage('editing')}
          className="text-xs px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/70 font-medium"
        >
          確認編輯
        </button>
        <button
          onClick={cancel}
          className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
        >
          取消
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
        {value || <span className="text-gray-400 dark:text-gray-500 italic">未設定</span>}
      </span>
      <button
        onClick={() => setStage('confirming')}
        className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        title="編輯（危險操作）"
      >
        編輯
      </button>
    </div>
  );
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [walletTarget, setWalletTarget] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    adminApi.getAccounts()
      .then(setUsers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    adminApi.getAccounts()
      .then(setUsers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q ||
      u.username.toLowerCase().includes(q) ||
      (u.ownerName ?? '').toLowerCase().includes(q);
  });

  const hasFilter = !!search;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">使用者管理</h2>
      </div>

      <div className="flex gap-3 mb-5 items-center flex-wrap">
        <div className="relative">
          <input
            type="text"
            placeholder="搜尋帳號或擁有者名稱"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
            >✕</button>
          )}
        </div>
        {hasFilter && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            顯示 {filtered.length} / {users.length} 筆
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-gray-500 dark:text-gray-400">載入中...</div>
      ) : error ? (
        <div className="text-red-500">載入失敗：{error}</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">操作</th>
                <th className="px-4 py-3 text-left">帳號</th>
                <th className="px-4 py-3 text-left">擁有者名稱</th>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">到期日</th>
                <th className="px-4 py-3 text-left">免費額度</th>
                <th className="px-4 py-3 text-left">Joleehi 里 ID</th>
                <th className="px-4 py-3 text-left">備注</th>
                <th className="px-4 py-3 text-left">建立時間</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map(user => (
                <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setWalletTarget(user)}
                        className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        點數
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{user.username}</td>
                  <td className="px-4 py-3">
                    <OwnerNameCell user={user} onSaved={load} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 font-mono">{user._id}</td>
                  <td className="px-4 py-3">
                    <ExpiresAtCell user={user} onSaved={load} />
                  </td>
                  <td className="px-4 py-3">
                    <FreeQuotaCell user={user} onSaved={load} />
                  </td>
                  <td className="px-4 py-3">
                    <JoleehiVillageIdCell user={user} onSaved={load} />
                  </td>
                  <td className="px-4 py-3 max-w-[160px]">
                    {user.note
                      ? <span className="text-xs text-amber-600 dark:text-amber-400 truncate block cursor-default" title={user.note}>{user.note}</span>
                      : <span className="text-xs text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {new Date(user.createdAt).toLocaleString('zh-TW')}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">無符合結果</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {walletTarget && (
        <UserWalletModal user={walletTarget} onClose={() => setWalletTarget(null)} />
      )}
    </div>
  );
};

export default UserManagement;
